import type { Node as RuntimeNode, Edge as RuntimeEdge } from '../../../core/models/scene';
import { getRelationshipMapping } from '../../../lib/diagram/registries';
import type { GraphEdge, GraphNode } from '../../../lib/graph/types';
import {
  attrsWithRelationSemantics,
  relationSemanticsForSubtype,
  relationSubtypeForPredicate,
  relationSubtypeFromAttrs,
  storedRelationSemantics,
  stripRelationInternalAttrs,
} from '../../../lib/graph/relationshipSemantics';

export interface GenogramHydrator {
  toUniversal: (nodes: RuntimeNode[], edges: RuntimeEdge[]) => { nodes: GraphNode[]; edges: GraphEdge[] };
  fromUniversal: (graph: { nodes: GraphNode[]; edges: GraphEdge[] }) => { nodes: RuntimeNode[]; edges: RuntimeEdge[] };
}

export const genogramHydrator: GenogramHydrator = {
  toUniversal(nodes, edges) {
    const uNodes: GraphNode[] = nodes.map((n) => {
      const attrs = (n.attrs as Record<string, unknown>) ?? {};
      const props = (n.props as Record<string, unknown>) ?? {};
      const mergedProps: Record<string, unknown> = {
        name: n.name ?? props.name ?? attrs.name,
        sex: n.sex ?? props.sex ?? attrs.sex,
        deceased: n.deceased ?? props.deceased ?? attrs.deceased,
        birthYear: n.birthYear ?? props.birthYear ?? attrs.birthYear,
        deathYear: n.deathYear ?? props.deathYear ?? attrs.deathYear,
        location: (n as any).location ?? props.location ?? attrs.location,
        ...attrs,
        ...props,
      };
      delete mergedProps.dataBinding;
      delete mergedProps.attrs;

      return {
        id: n.id,
        type: 'person',
        label: n.name ?? (mergedProps.name as string | undefined),
        props: mergedProps,
      };
    });
    const uEdges: GraphEdge[] = edges.map((e) => {
      const attrs = (e.attrs as Record<string, unknown> | undefined) ?? {};
      const subtypeId = relationSubtypeFromAttrs('genogram', e.type as string, attrs) ?? (e.type as string);
      const props = stripRelationInternalAttrs(attrs);
      return {
        id: e.id,
        type: e.type as string,
        from: e.from,
        to: e.to,
        props,
        semantics: relationSemanticsForSubtype({
          diagramType: 'genogram',
          subtypeId,
          qualifierSource: props,
          fallbackPredicate: subtypeId,
          originalLabel: subtypeId,
          existing: storedRelationSemantics(attrs),
        }),
      };
    });
    return { nodes: uNodes, edges: uEdges };
  },
  fromUniversal(graph) {
    const nodes: RuntimeNode[] = graph.nodes.map((n, index) => {
      const props = (n.props as Record<string, unknown>) ?? {};
      return {
        id: n.id,
        type: 'person',
        name: (n.label as string | undefined) ?? (props.name as string | undefined),
        sex: (props.sex as any) ?? undefined,
        deceased: props.deceased as boolean | undefined,
        birthYear: props.birthYear as number | undefined,
        deathYear: props.deathYear as number | undefined,
        location: props.location as string | undefined,
        x: 120 + (index % 5) * 180,
        y: 120 + Math.floor(index / 5) * 140,
        w: 60,
        h: 60,
        attrs: props,
        props,
      } as any;
    });
    const edges: RuntimeEdge[] = graph.edges.map((e) => {
      const subtypeId = typeof e.semantics?.originalLabel === 'string'
        ? e.semantics.originalLabel
        : typeof e.semantics?.predicate === 'string'
          ? relationSubtypeForPredicate('genogram', e.semantics.predicate) ?? e.type as string
          : e.type as string;
      const mapping = getRelationshipMapping('genogram', subtypeId);
      const edgeType = mapping?.edgeType || (e.type as string);
      const defaultEdgeProps = (mapping?.defaultEdgeProps ?? {}) as Record<string, unknown>;
      return {
        id: e.id,
        type: edgeType as any,
        from: e.from,
        to: e.to,
        attrs: attrsWithRelationSemantics({
          ...defaultEdgeProps,
          ...(e.props as Record<string, unknown>),
        }, e.semantics, subtypeId),
      } as any;
    });
    return { nodes, edges };
  }
};
